import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';
import { nanoid } from 'nanoid';
import { uploadAttachment } from '../features/attachments.js';

/**
 * Paste/drop of image files: insert an ![[uploading-…]] placeholder at the
 * cursor, upload via the attachments feature, then swap the placeholder for
 * the real ![[path]] embed (or remove it on failure).
 */

function replaceToken(view: EditorView, token: string, replacement: string): void {
  if (!view.dom.isConnected) return;
  const i = view.state.doc.toString().indexOf(token);
  if (i === -1) return;
  view.dispatch({
    changes: { from: i, to: i + token.length, insert: replacement },
    userEvent: 'input',
  });
}

async function handleUpload(view: EditorView, file: File): Promise<void> {
  const token = `![[uploading-${nanoid(8)}]]`;
  const { from, to } = view.state.selection.main;
  view.dispatch({
    changes: { from, to, insert: token },
    selection: { anchor: from + token.length },
    userEvent: 'input.paste',
  });
  try {
    const path = await uploadAttachment(file);
    replaceToken(view, token, `![[${path}]]`);
  } catch {
    replaceToken(view, token, '');
  }
}

function imageFiles(list: FileList | undefined | null): File[] {
  return Array.from(list ?? []).filter((f) => f.type.startsWith('image/'));
}

export function imagePasteDrop(): Extension {
  return EditorView.domEventHandlers({
    paste(e, view) {
      const files = imageFiles(e.clipboardData?.files);
      if (files.length === 0) return false;
      e.preventDefault();
      for (const f of files) void handleUpload(view, f);
      return true;
    },
    drop(e, view) {
      const files = imageFiles(e.dataTransfer?.files);
      if (files.length === 0) return false;
      e.preventDefault();
      const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
      if (pos != null) view.dispatch({ selection: { anchor: pos } });
      for (const f of files) void handleUpload(view, f);
      return true;
    },
  });
}
